import { supabase } from "../supabaseClient";
import { getErrorMessage } from "./errorUtils";
import { fetchWithRetry } from "../lib/fetchWithRetry";

export class ApiError extends Error {
  status: number;
  statusText: string;
  correlationId: string | null;
  data: any;

  constructor(message: string, status: number, statusText: string, correlationId: string | null, data: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.correlationId = correlationId;
    this.data = data;
  }
}

export interface RequestOptions extends RequestInit {
  timeout?: number;
  externalOrigin?: string;
  skipDeduplication?: boolean;
}

const inFlightRequests = new Map<string, Promise<any>>();
let cachedToken: string | null = null;
let tokenExpiresAt = 0;

export function clearApiTokenCache() {
  cachedToken = null;
  tokenExpiresAt = 0;
  inFlightRequests.clear();
}

function generateCorrelationId(): string {
  return "corr-" + Math.random().toString(36).substring(2, 15) + "-" + Date.now();
}

async function getValidToken(): Promise<string | null> {
  // If we have a cached token that is not expired, use it
  if (cachedToken && Date.now() < tokenExpiresAt - 60000) {
    return cachedToken;
  }
  try {
    if (supabase) {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        cachedToken = data.session.access_token;
        tokenExpiresAt = (data.session.expires_at || 0) * 1000;
        return cachedToken;
      }
    }
  } catch (err) {
    console.warn("apiClient: Failed to retrieve session:", err);
  }

  // LocalStorage fallback
  try {
    const stored = localStorage.getItem("Business_scout_user");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.token) {
        return parsed.token;
      }
    }
  } catch {}
  return null;
}

async function forceTokenRefresh(): Promise<string | null> {
  try {
    if (supabase) {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data?.session) {
        cachedToken = data.session.access_token;
        tokenExpiresAt = (data.session.expires_at || 0) * 1000;
        
        // Update local storage representation
        const stored = localStorage.getItem("Business_scout_user");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            parsed.token = cachedToken;
            localStorage.setItem("Business_scout_user", JSON.stringify(parsed));
          } catch {}
        }
        return cachedToken;
      }
    }
  } catch (err) {
    console.warn("apiClient: Force session refresh failed:", err);
  }
  return null;
}

function getDeduplicationKey(method: string, url: string, userId: string | null, body?: any): string {
  const bodyPart = body ? JSON.stringify(body) : "";
  return `${method}:${url}:${userId || "anonymous"}:${bodyPart}`;
}

async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// GET retry logic with exponential backoff + jitter
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 500
): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    const isTransient = !error.status || [429, 500, 502, 503, 504].includes(error.status);
    if (retries <= 0 || !isTransient) {
      throw error;
    }
    const backoff = delay * Math.pow(2, 3 - retries) + Math.random() * 100;
    await wait(backoff);
    return retryWithBackoff(fn, retries - 1, delay);
  }
}

/**
 * Perform an HTTP fetch request against the application's backend.
 */
async function performRequest(url: string, options: RequestOptions = {}): Promise<any> {
  const method = (options.method || "GET").toUpperCase();
  const correlationId = generateCorrelationId();
  const headers = new Headers(options.headers);

  // Generate and attach request correlation id
  headers.set("X-Correlation-ID", correlationId);

  // Set Authorization Bearer header if available
  const token = await getValidToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Set default Content-Type for requests with body
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const origin = options.externalOrigin || "";
  const requestUrl = url.startsWith("/") ? `${origin}${url}` : url;

  // Timeout handling using AbortController
  const controller = new AbortController();
  const signal = options.signal || controller.signal;
  const timeoutId = options.timeout
    ? setTimeout(() => controller.abort(), options.timeout)
    : null;

  try {
    const response = await fetchWithRetry(requestUrl, {
      ...options,
      headers,
      signal,
      timeout: options.timeout || 15000,
    });

    if (timeoutId) clearTimeout(timeoutId);

    // Safe handling of empty / no-content response bodies
    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get("Content-Type") || "";
    let data: any = null;

    if (contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      if (typeof data === "string" && (data.trim().startsWith("<") || data.toLowerCase().includes("<html>"))) {
        const userMsg = response.status === 403 
          ? "Access denied. Please sign in again or verify your permissions." 
          : `Server returned HTTP ${response.status} error.`;
        data = { error: userMsg, isHtml: true, status: response.status };
      }

      const errorMessage = getErrorMessage(data, `HTTP Error ${response.status}: ${response.statusText}`);

      throw new ApiError(
        errorMessage,
        response.status,
        response.statusText,
        correlationId,
        data
      );
    }

    return data;
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new ApiError("Request timed out", 408, "Request Timeout", correlationId, null);
    }
    throw err;
  }
}

/**
 * Centralized API client.
 */
export async function apiRequest(url: string, options: RequestOptions = {}): Promise<any> {
  const method = (options.method || "GET").toUpperCase();

  // Retrieve user ID to scope deduplication caching accurately
  let userId: string | null = null;
  try {
    const stored = localStorage.getItem("Business_scout_user");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.id) userId = parsed.id;
    }
  } catch {}

  const dedupeKey = getDeduplicationKey(method, url, userId, options.body);

  // Core Request Logic with 401 Auto-Refresh
  const executeWithRefresh = async () => {
    try {
      return await performRequest(url, options);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 401) {
        console.warn(`apiClient: 401 Unauthorized for ${url}. Attempting token refresh...`);
        const refreshedToken = await forceTokenRefresh();
        if (refreshedToken) {
          // Retry request exactly once with new token
          return await performRequest(url, options);
        }
      }
      throw err;
    }
  };

  // Deduping + Retrying for GET requests
  if (method === "GET" && !options.skipDeduplication) {
    let inFlight = inFlightRequests.get(dedupeKey);
    if (!inFlight) {
      inFlight = retryWithBackoff(() => executeWithRefresh());
      inFlightRequests.set(dedupeKey, inFlight);

      // Clean cache when finished
      inFlight.then(
        () => inFlightRequests.delete(dedupeKey),
        () => inFlightRequests.delete(dedupeKey)
      );
    }
    return inFlight;
  }

  // Standard non-GET or non-deduped requests
  return executeWithRefresh();
}

export const apiClient = {
  get: (url: string, options?: RequestOptions) => apiRequest(url, { ...options, method: "GET" }),
  post: (url: string, body?: any, options?: RequestOptions) =>
    apiRequest(url, { ...options, method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: (url: string, body?: any, options?: RequestOptions) =>
    apiRequest(url, { ...options, method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: (url: string, body?: any, options?: RequestOptions) =>
    apiRequest(url, { ...options, method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: (url: string, options?: RequestOptions) => apiRequest(url, { ...options, method: "DELETE" }),
};
